/**
 * What a frame costs, in milliseconds: the CPU's share and the GPU's.
 *
 * The frame rate tops out at the screen's refresh — 60 on a 60 Hz panel —
 * however little a frame costs, so it says nothing about headroom. These do:
 * a frame fits while the larger of the two stays under 1000 / refresh ms.
 *
 * The CPU time is `frame()` from start to end. The GPU time is a timer query
 * (EXT_disjoint_timer_query_webgl2) around the world's render — shadows,
 * scene and post — read back a few frames later, when the GPU has done it.
 * Without the extension, or while a query is lost to a GPU context switch
 * ("disjoint"), the GPU time is simply not shown.
 */

interface TimerExt {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

/** Reported over windows of this long, ms: the average, and the worst frame in it. */
const WINDOW = 500;

class Window {
  private sum = 0;
  private count = 0;
  private peak = 0;
  avg = 0;
  max = 0;

  add(ms: number): void {
    this.sum += ms;
    this.count++;
    this.peak = Math.max(this.peak, ms);
  }

  close(): void {
    if (this.count === 0) return;
    this.avg = this.sum / this.count;
    this.max = this.peak;
    this.sum = 0;
    this.count = 0;
    this.peak = 0;
  }
}

export class FrameTimer {
  private readonly ext: TimerExt | null;
  private readonly pending: WebGLQuery[] = [];
  private active: WebGLQuery | undefined;
  private frameStart = 0;
  private windowStart = performance.now();
  private readonly cpuWindow = new Window();
  private readonly gpuWindow = new Window();
  private gpuSeen = false;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as TimerExt | null;
  }

  beginFrame(): void {
    this.frameStart = performance.now();
    this.collect();
  }

  /** Around the GPU work to time; one pair a frame, not nested. */
  beginGpu(): void {
    if (this.ext === null || this.active !== undefined || this.pending.length >= 4) return;
    const query = this.gl.createQuery();
    if (query === null) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, query);
    this.active = query;
  }

  endGpu(): void {
    if (this.ext === null || this.active === undefined) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = undefined;
  }

  endFrame(): void {
    const now = performance.now();
    this.cpuWindow.add(now - this.frameStart);
    if (now - this.windowStart >= WINDOW) {
      this.windowStart = now;
      this.cpuWindow.close();
      this.gpuWindow.close();
    }
  }

  /** The HUD line: average and worst of the last window. */
  text(): string {
    const ms = (w: Window) => `${w.avg.toFixed(1)} ms (max ${w.max.toFixed(1)})`;
    return `cpu ${ms(this.cpuWindow)}` + (this.gpuSeen ? `   gpu ${ms(this.gpuWindow)}` : '   gpu n/a');
  }

  /** Read back the queries the GPU has finished, oldest first. */
  private collect(): void {
    const { gl, ext } = this;
    if (ext === null) return;
    // A disjoint event — the GPU switched context, throttled — spoils every
    // query in flight.
    const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean;
    while (this.pending.length > 0) {
      const query = this.pending[0];
      if (!disjoint && gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE) !== true) break;
      this.pending.shift();
      if (!disjoint) {
        this.gpuWindow.add((gl.getQueryParameter(query, gl.QUERY_RESULT) as number) / 1e6);
        this.gpuSeen = true;
      }
      gl.deleteQuery(query);
    }
  }
}
